import React from 'react';
import { AlertCircle, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import { cn } from '@/lib/utils';

interface AnomalyIndicatorProps {
  anomalyResult: AnomalyDetectionResult;
  onClick?: () => void;
  showCleanIndicator?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function AnomalyIndicator({
  anomalyResult,
  onClick,
  showCleanIndicator = false,
  size = 'md',
}: AnomalyIndicatorProps) {
  const { anomalies, highPriorityCount, mediumPriorityCount, lowPriorityCount, hasAnomalies } = anomalyResult;

  // Determine the highest severity
  const highestSeverity = highPriorityCount > 0 ? 'high' : mediumPriorityCount > 0 ? 'medium' : 'low';

  // Get icon based on severity
  const getIcon = () => {
    if (!hasAnomalies) {
      return <CheckCircle className={cn(
        'text-green-600 dark:text-green-400',
        size === 'sm' && 'h-4 w-4',
        size === 'md' && 'h-5 w-5',
        size === 'lg' && 'h-6 w-6'
      )} />;
    }

    const iconClass = cn(
      size === 'sm' && 'h-4 w-4',
      size === 'md' && 'h-5 w-5',
      size === 'lg' && 'h-6 w-6'
    );

    switch (highestSeverity) {
      case 'high':
        return <AlertCircle className={cn(iconClass, 'text-red-600 dark:text-red-400')} />;
      case 'medium':
        return <AlertTriangle className={cn(iconClass, 'text-amber-600 dark:text-amber-400')} />;
      case 'low':
        return <Info className={cn(iconClass, 'text-blue-600 dark:text-blue-400')} />;
    }
  };

  // Get badge variant
  const getBadgeVariant = () => {
    switch (highestSeverity) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Group anomalies by severity
  const highPriorityAnomalies = anomalies.filter(a => a.severity === 'high');
  const mediumPriorityAnomalies = anomalies.filter(a => a.severity === 'medium');
  const lowPriorityAnomalies = anomalies.filter(a => a.severity === 'low');

  // If no anomalies and not showing clean indicator, return null
  if (!hasAnomalies && !showCleanIndicator) {
    return null;
  }

  const totalCount = highPriorityCount + mediumPriorityCount + lowPriorityCount;

  // Simple indicator without tooltip - details shown in View dialog
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md transition-all',
        onClick && 'cursor-pointer hover:opacity-80',
        !onClick && 'cursor-default'
      )}
      aria-label={hasAnomalies ? `${totalCount} issue${totalCount !== 1 ? 's' : ''} detected` : 'No issues detected'}
    >
      {getIcon()}
      {hasAnomalies && totalCount > 1 && (
        <Badge 
          variant={getBadgeVariant()} 
          className={cn(
            'h-5 min-w-[20px] px-1.5 text-xs font-semibold',
            size === 'sm' && 'h-4 min-w-[16px] px-1 text-[10px]',
            size === 'lg' && 'h-6 min-w-[24px] px-2 text-sm'
          )}
        >
          {totalCount}
        </Badge>
      )}
    </button>
  );
}
